import { injectGlobal } from "@emotion/css";
import { Color } from "@tiptap/extension-color";
import { TextStyle } from "@tiptap/extension-text-style";
import { Typography } from "@tiptap/extension-typography";
import { Placeholder } from "@tiptap/extensions";
import { type UseEditorOptions, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";

import { FontSizes } from "@/assets/fonts.ts";

const H1_UNDERLINE_THICKNESS_PX = 4;

type ExtensionsOptions = {
  placeholder?: string;
};
export const richTextExtensions = (options: ExtensionsOptions = {}) => [
  StarterKit.configure({
    bulletList: {
      HTMLAttributes: {
        style: "list-style-position: inside;",
      },
    },
    heading: {
      levels: [5],
    },
  }),
  Typography,
  TextStyle,
  Color,
  Placeholder.configure({ placeholder: options.placeholder }),
];

type UseRichTextEditorOptions = Omit<UseEditorOptions, "extensions"> & {
  extensions?: ExtensionsOptions;
};

export const useRichTextEditor = ({ extensions, ...options }: UseRichTextEditorOptions) => {
  return useEditor({
    extensions: richTextExtensions(extensions),
    ...options,
  });
};

/**
 *   h1: { fontSize: FontSizes.h1, fontWeight: 700, color: palette.text.title },
 *   h2: { fontSize: FontSizes.h2, fontWeight: 700, color: palette.text.title },
 *   h3: { fontSize: FontSizes.h3, fontWeight: 700, color: palette.text.title },
 *   h4: { fontSize: FontSizes.h4, fontWeight: 700, color: palette.text.title },
 *   h5: { fontSize: FontSizes.h5, fontWeight: 700, color: palette.text.title },
 *   h6: { fontSize: FontSizes.h6, fontWeight: 700, color: palette.text.primary }
 */
injectGlobal`
.tiptap p {
  font-size: ${FontSizes.body};
  font-family: NunitoSans, Roboto, sans-serif;
  display: block;
  color: var(${"--mui-palette-text-primary"});
}
.tiptap h1 {
  font-size: ${FontSizes.h1};
  font-weight: 700;
  font-family: NunitoSans, Roboto, sans-serif;
  color: var(${"--mui-palette-text-title"});
  margin: calc(${FontSizes.h1} / 3) 0;
  border-bottom: ${H1_UNDERLINE_THICKNESS_PX}px solid var(${"--mui-palette-divider"});
    
}
.tiptap h2 {
  font-size: ${FontSizes.h2};
  font-weight: 700;
  font-family: NunitoSans, Roboto, sans-serif;
  color: var(${"--mui-palette-text-title"});
  margin: calc(${FontSizes.h2} / 2) 0;
}
.tiptap h3 {
  font-size: ${FontSizes.h3};
  font-weight: 700;
  font-family: NunitoSans, Roboto, sans-serif;
  color: var(${"--mui-palette-text-title"});
  margin: calc(${FontSizes.h3} / 2) 0;
}
.tiptap h4 {
  font-size: ${FontSizes.h4};
  font-weight: 700;
  font-family: NunitoSans, Roboto, sans-serif;
  color: var(${"--mui-palette-text-title"});
  margin: calc(${FontSizes.h4} / 2) 0;
}
.tiptap h5 {
  font-size: ${FontSizes.h5};
  font-weight: 700;
  font-family: NunitoSans, Roboto, sans-serif;
  color: var(${"--mui-palette-text-title"});
  margin: calc(${FontSizes.h5} / 2) 0;
}
.tiptap h6 {
  font-size: ${FontSizes.h6};
  font-weight: 700;
  font-family: NunitoSans, Roboto, sans-serif;
  color: var(${"--mui-palette-text-title"});
  margin: calc(${FontSizes.h6} / 2) 0;
}
.tiptap p.is-editor-empty:first-child::before {
    color: #adb5bd;
    content: attr(data-placeholder);
    float: left;
    height: 0;
    pointer-events: none;
}
`;
